import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchApi } from '../lib/api';

export default function OrgTeam() {
  const { org, isOrgAdmin } = useAuth();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [membersRes, invitesRes] = await Promise.all([
        fetchApi('/api/org/members'),
        isOrgAdmin ? fetchApi('/api/org/invitations').catch(() => ({ invitations: [] })) : { invitations: [] },
      ]);
      setMembers(membersRes.members || []);
      setInvitations(invitesRes.invitations || []);
    } catch (err) {
      console.error('Load team error:', err);
    }
    setLoading(false);
  }, [isOrgAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await fetchApi('/api/org/invite', {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });
      setInviteEmail('');
      setShowInvite(false);
      loadData();
    } catch (err) {
      alert('Failed to invite: ' + err.message);
    }
    setInviting(false);
  };

  const handleRemove = async (memberId, name) => {
    if (!window.confirm(`Remove ${name} from the team? They will lose access to all org features.`)) return;
    try {
      await fetchApi(`/api/org/members/${memberId}`, { method: 'DELETE' });
      loadData();
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  };

  const handleRoleChange = async (memberId, newRole) => {
    try {
      await fetchApi(`/api/org/members/${memberId}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole }),
      });
      loadData();
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  };

  if (loading) return <div className="text-sm text-gray-400 py-8 text-center">Loading team...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl text-vault-navy">Team</h1>
          <p className="text-sm text-gray-400 mt-1">{org?.company_name} — {members.length} member{members.length !== 1 ? 's' : ''}</p>
        </div>
        {isOrgAdmin && (
          <button
            onClick={() => setShowInvite(!showInvite)}
            className="px-5 py-2.5 rounded-btn text-sm font-semibold text-white"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
          >
            Invite Member
          </button>
        )}
      </div>

      {/* Invite form */}
      {showInvite && isOrgAdmin && (
        <div className="glass p-5 mb-5 max-w-lg">
          <h3 className="text-sm font-semibold text-vault-navy mb-3">Invite a Team Member</h3>
          <div className="space-y-3">
            <input
              className="w-full px-4 py-3 rounded-input border-2 border-vault-border text-sm text-vault-navy bg-white/70 focus:border-vault-gold focus:outline-none"
              placeholder="Email address"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
            />
            <div className="flex gap-2">
              <select
                className="px-3 py-2.5 rounded-input border-2 border-vault-border text-sm text-gray-500 bg-white"
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value)}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
              <button
                onClick={handleInvite}
                disabled={inviting || !inviteEmail.trim()}
                className="flex-1 py-2.5 rounded-btn text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
              >
                {inviting ? 'Sending...' : 'Send Invite'}
              </button>
              <button onClick={() => setShowInvite(false)} className="px-4 py-2.5 rounded-btn text-sm text-gray-400 border-2 border-vault-border">
                Cancel
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {inviteRole === 'admin' ? 'Admins can manage billing, team members, and org settings.' : 'Members can manage their own wish list, pipeline, and attend events.'}
          </p>
        </div>
      )}

      {/* Members list */}
      <div className="glass overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-vault-border">
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase">Name</th>
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase">Email</th>
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase">Role</th>
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase">Joined</th>
              {isOrgAdmin && <th className="text-right px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-vault-border">
            {members.map(m => (
              <tr key={m.id} className="hover:bg-gray-50/50">
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-vault-gold to-amber-500 flex items-center justify-center text-white font-semibold text-xs">
                      {m.display_name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <span className="font-medium text-vault-navy">{m.display_name}</span>
                  </div>
                </td>
                <td className="px-5 py-4 text-gray-400">{m.email}</td>
                <td className="px-5 py-4">
                  {isOrgAdmin ? (
                    <select
                      className="text-xs border border-vault-border rounded px-2 py-1 bg-white"
                      value={m.org_role}
                      onChange={e => handleRoleChange(m.id, e.target.value)}
                    >
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                    </select>
                  ) : (
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                      m.org_role === 'admin' ? 'bg-vault-gold/10 text-vault-gold' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {m.org_role === 'admin' ? 'Admin' : 'Member'}
                    </span>
                  )}
                </td>
                <td className="px-5 py-4 text-gray-400 text-xs">
                  {new Date(m.created_at).toLocaleDateString()}
                </td>
                {isOrgAdmin && (
                  <td className="px-5 py-4 text-right">
                    <button
                      onClick={() => handleRemove(m.id, m.display_name)}
                      className="text-xs text-gray-300 hover:text-vault-error transition-colors"
                    >
                      Remove
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pending invitations */}
      {isOrgAdmin && invitations.filter(i => !i.claimed_at).length > 0 && (
        <div className="mt-5">
          <h3 className="text-sm font-semibold text-gray-400 uppercase mb-3">Pending Invitations</h3>
          <div className="space-y-2">
            {invitations.filter(i => !i.claimed_at).map(inv => (
              <div key={inv.id} className="glass p-4 flex items-center justify-between">
                <div>
                  <span className="text-sm text-vault-navy font-medium">{inv.invited_email}</span>
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                    inv.role === 'admin' ? 'bg-vault-gold/10 text-vault-gold' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {inv.role}
                  </span>
                </div>
                <span className="text-xs text-gray-400">
                  Expires {new Date(inv.expires_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pricing note */}
      <div className="mt-5 glass p-4 border-2 border-dashed border-vault-border">
        <p className="text-xs text-gray-400">
          <strong>Pricing:</strong> $349/month base (1 seat included) + $99/month per additional team member.
          {isOrgAdmin ? ' Manage billing from the Billing tab.' : ' Contact your org admin for billing questions.'}
        </p>
      </div>
    </div>
  );
}
